import {
  BehavioralEnrollment,
  ChargerEnrollment,
  Enrollment,
  EnrollmentVerificationStatusBgColor,
  EnrollmentVerificationStatusColor,
  ThermostatEnrollment,
  UtilityProgram,
  VehicleEnrollment,
} from '@/types/utilityProgram'
import { ResourceModelQueryResult, RootState } from '@/request/types'
import { UtilityProgramEnrollment } from '@/types'
import { ID } from '@/types/model'
import { match, P } from 'ts-pattern'
import {
  EnergySystemEnrollment,
  EnrollmentOverview,
  EnrollmentRoot,
  BehavioralEnrollment as BehavioralEnrollmentNew,
  ChargerEnrollment as ChargerEnrollmentNew,
  EnergySystemEnrollment as EnergySystemEnrollmentNew,
  VehicleEnrollment as VehicleEnrollmentNew,
  ThermostatEnrollment as ThermostatEnrollmentNew,
} from '@/types/programs-new/programs-new'
import {
  behavioralEnrollmentCollection,
  chargerEnrollmentCollection,
  energySystemEnrollmentCollection,
  thermostatEnrollmentCollection,
  vehicleEnrollmentCollection,
} from '@/reducers/deviceProgramEnrollments'
import { logException } from '@/logging/sentry'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { VerificationStatusTagType } from '@/hooks/useDeviceVerificationStatus/useDeviceVerificationStatus'

export function userHasUnenrolledBetterProgram(
  currentEnrollments: ResourceModelQueryResult<UtilityProgramEnrollment>[],
  potentialProgram?: UtilityProgram,
) {
  if (!potentialProgram) {
    return false
  }

  if (currentEnrollments.length > 0) {
    return potentialProgram.id !== currentEnrollments[0].utility_program?.id
  }

  return true
}

export function getEnrollmentDeviceId(
  enrollment:
    | Enrollment
    | VehicleEnrollment
    | ChargerEnrollment
    | BehavioralEnrollment
    | ThermostatEnrollment,
): ID | null {
  return match(enrollment)
    .with({ vehicle_id: P.number.int() }, (e) => e.vehicle_id)
    .with({ charger_id: P.number.int() }, (e) => e.charger_id)
    .with({ profile_id: P.number.int() }, (e) => e.profile_id)
    .with({ thermostat_id: P.number.int() }, (e) => e.thermostat_id)
    .otherwise(() => {
      logException(
        `Enrollment given did not contain expected device ID: ${JSON.stringify(
          enrollment,
        )}`,
      )

      return null
    })
}

export function toggleProgramEnrollmentAction(
  enrollment:
    | EnrollmentRoot
    | VehicleEnrollment
    | ChargerEnrollment
    | BehavioralEnrollment
    | ThermostatEnrollment
    | EnergySystemEnrollment,
): ((dispatch: any, getState: () => RootState) => Promise<any>) | null {
  const unenrolledAt = enrollment.enrolled ? new Date().toISOString() : null

  return match(enrollment)
    .with({ vehicle_id: P.number.int() }, (vehicleEnrollment) => {
      return vehicleEnrollmentCollection.actions.update(enrollment.id, {
        unenrolled_at: unenrolledAt,
        vehicle_id: vehicleEnrollment.vehicle_id,
      })
    })
    .with({ charger_id: P.number.int() }, (chargerEnrollment) => {
      return chargerEnrollmentCollection.actions.update(enrollment.id, {
        unenrolled_at: unenrolledAt,
        charger_id: chargerEnrollment.charger_id,
      })
    })
    .with({ thermostat_id: P.number.int() }, (thermostatEnrollment) => {
      return thermostatEnrollmentCollection.actions.update(enrollment.id, {
        unenrolled_at: unenrolledAt,
        thermostat_id: thermostatEnrollment.thermostat_id,
      })
    })
    .with({ profile_id: P.number.int() }, (behavioralEnrollment) => {
      return behavioralEnrollmentCollection.actions.update(enrollment.id, {
        unenrolled_at: unenrolledAt,
        profile_id: behavioralEnrollment.profile_id,
      })
    })
    .with({ energy_system_id: P.string }, (energySystemEnrollment) => {
      return energySystemEnrollmentCollection.actions.update(enrollment.id, {
        unenrolled_at: unenrolledAt,
        energy_system_id: energySystemEnrollment.energy_system_id,
      })
    })
    .otherwise(() => null)
}

export function createDeviceEnrollmentAction(
  deviceType: DeviceType,
  utilityProgramId: number,
  deviceId: string | number,
) {
  return match(deviceType)
    .with('vehicle', () =>
      vehicleEnrollmentCollection.actions.create({
        utility_program_id: utilityProgramId,
        vehicle_id: Number(deviceId),
      }),
    )
    .with('vehicleCharger', () =>
      chargerEnrollmentCollection.actions.create({
        utility_program_id: utilityProgramId,
        charger_id: Number(deviceId),
      }),
    )
    .with('thermostat', () =>
      thermostatEnrollmentCollection.actions.create({
        utility_program_id: utilityProgramId,
        thermostat_id: Number(deviceId),
      }),
    )
    .with('home', () =>
      behavioralEnrollmentCollection.actions.create({
        utility_program_id: utilityProgramId,
        profile_id: Number(deviceId),
      }),
    )
    .with('energySystem', () =>
      energySystemEnrollmentCollection.actions.create({
        utility_program_id: utilityProgramId,
        energy_system_id: String(deviceId),
      }),
    )
    .exhaustive()
}

const verificationStatusToDisplay = (
  status: VerificationStatusTagType,
): {
  text: string
  color: EnrollmentVerificationStatusColor
  bgColor: EnrollmentVerificationStatusBgColor
  visible: boolean
} => {
  return match(status)
    .with(VerificationStatusTagType.PENDING, () => ({
      text: 'Pending',
      color: EnrollmentVerificationStatusColor.PENDING,
      bgColor: EnrollmentVerificationStatusBgColor.PENDING,
      visible: true,
    }))
    .with(VerificationStatusTagType.ENROLLED, () => ({
      text: 'Enrolled',
      color: EnrollmentVerificationStatusColor.VERIFIED,
      bgColor: EnrollmentVerificationStatusBgColor.VERIFIED,
      visible: true,
    }))
    .with(VerificationStatusTagType.ELIGIBLE, () => ({
      text: 'Eligible',
      color: EnrollmentVerificationStatusColor.ELIGIBLE,
      bgColor: EnrollmentVerificationStatusBgColor.ELIGIBLE,
      visible: true,
    }))
    .with(VerificationStatusTagType.INELIGIBLE, () => ({
      text: 'Ineligible',
      color: EnrollmentVerificationStatusColor.INELIGIBLE,
      bgColor: EnrollmentVerificationStatusBgColor.INELIGIBLE,
      visible: true,
    }))
    .with(VerificationStatusTagType.AVAILABLE, () => ({
      text: 'Available',
      color: EnrollmentVerificationStatusColor.ELIGIBLE,
      bgColor: EnrollmentVerificationStatusBgColor.ELIGIBLE,
      visible: true,
    }))
    .with(VerificationStatusTagType.COMPLETED, () => ({
      text: 'Completed',
      color: EnrollmentVerificationStatusColor.VERIFIED,
      bgColor: EnrollmentVerificationStatusBgColor.VERIFIED,
      visible: true,
    }))
    .with(VerificationStatusTagType.INACTIVE, () => ({
      text: 'Ended',
      color: EnrollmentVerificationStatusColor.INELIGIBLE,
      bgColor: EnrollmentVerificationStatusBgColor.INELIGIBLE,
      visible: true,
    }))
    .exhaustive()
}

// Function overloads for specific return types
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: 'vehicle',
): VehicleEnrollmentNew | undefined
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: 'thermostat',
): ThermostatEnrollmentNew | undefined
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: 'vehicleCharger',
): ChargerEnrollmentNew | undefined
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: 'home',
): BehavioralEnrollmentNew | undefined
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: 'energySystem',
): EnergySystemEnrollmentNew | undefined
// General overload for any device type
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: DeviceType,
): EnrollmentRoot | undefined
export function getDeviceEnrollment(
  enrollmentOverview: EnrollmentOverview | undefined,
  deviceId: number | string,
  deviceType: DeviceType,
): EnrollmentRoot | undefined {
  if (!enrollmentOverview) {
    return undefined
  }

  return match(deviceType)
    .with('vehicle', () =>
      enrollmentOverview.vehicle_enrollments.find(
        (enrollment) => enrollment.vehicle_id == deviceId,
      ),
    )
    .with('thermostat', () =>
      enrollmentOverview.thermostat_enrollments.find(
        (enrollment) => enrollment.thermostat_id == deviceId,
      ),
    )
    .with('vehicleCharger', () =>
      enrollmentOverview.charger_enrollments.find(
        (enrollment) => enrollment.charger_id == deviceId,
      ),
    )
    .with('home', () =>
      enrollmentOverview.behavioral_enrollments.find(
        (enrollment) => enrollment.profile_id == deviceId,
      ),
    )
    .with('energySystem', () =>
      enrollmentOverview.energy_system_enrollments.find(
        (enrollment) => enrollment.energy_system_id == String(deviceId),
      ),
    )
    .exhaustive()
}

const hasSomeDeviceEnrolled = (
  enrollmentOverview: EnrollmentOverview | undefined | null,
) => {
  return match(enrollmentOverview)
    .with(P.nullish, () => false)
    .otherwise((overview) =>
      [
        overview.behavioral_enrollments,
        overview.charger_enrollments,
        overview.vehicle_enrollments,
        overview.thermostat_enrollments,
        overview.energy_system_enrollments,
      ].some((arr) => (arr?.length ?? 0) > 0),
    )
}

const UtilityProgramHelpers = {
  userHasUnenrolledBetterProgram,
  getEnrollmentDeviceId,
  toggleProgramEnrollmentAction,
  verificationStatusToDisplay,
  getDeviceEnrollment,
  hasSomeDeviceEnrolled,
}

export { hasSomeDeviceEnrolled }
export default UtilityProgramHelpers
